import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  VendorTrustApiLinkDTO,
  VendorTrustApiPreviewDTO,
  VendorTrustApiPreviewItemDTO,
} from '@deepguide-ai/types'
import { Link2, Loader2, RefreshCw, ShieldCheck, ShieldAlert, Unlink, Download } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { Input } from '@deepguide-ai/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@deepguide-ai/ui/table'
import { DOC_TYPE_LABELS } from '@/pages/vendors/doc-type-labels'

/**
 * RFC-228 §7 — "Fetch from Trust Center" on a vendor.
 *
 * Link a vendor's Trust API, preview the resource → evidence-request mapping
 * (the human confirms every docType before anything attaches), request a
 * grant for gated documents, and run the fetch as a background job.
 */
export function VendorTrustApiCard({
  vendorId,
  readOnly,
  onDocumentsChanged,
}: {
  vendorId: string
  readOnly: boolean
  onDocumentsChanged: () => void
}) {
  const queryClient = useQueryClient()
  const [url, setUrl] = useState('')
  const [previewOpen, setPreviewOpen] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [docTypes, setDocTypes] = useState<Record<string, string>>({})
  const [ndaAccepted, setNdaAccepted] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  // Latest callback without re-arming the completion effect on every render.
  const onDocumentsChangedRef = useRef(onDocumentsChanged)
  useEffect(() => {
    onDocumentsChangedRef.current = onDocumentsChanged
  }, [onDocumentsChanged])

  const linkQuery = useQuery(
    trpc.vendorManagement.trustApiGetLink.queryOptions({ vendorId })
  )
  const link = (linkQuery.data ?? null) as VendorTrustApiLinkDTO | null

  const previewQuery = useQuery({
    ...trpc.vendorManagement.trustApiPreview.queryOptions({ vendorId }),
    enabled: previewOpen && Boolean(link),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const preview = (previewQuery.data ?? null) as VendorTrustApiPreviewDTO | null

  const statusQuery = useQuery({
    ...trpc.vendorManagement.trustApiFetchStatus.queryOptions({ jobId: jobId ?? '' }),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const state = (query.state.data as { state?: string } | undefined)?.state
      return state === 'queued' || state === 'running' ? 2000 : false
    },
  })

  const setLinkMutation = useMutation(trpc.vendorManagement.trustApiSetLink.mutationOptions())
  const removeLinkMutation = useMutation(trpc.vendorManagement.trustApiRemoveLink.mutationOptions())
  const requestAccessMutation = useMutation(trpc.vendorManagement.trustApiRequestAccess.mutationOptions())
  const checkAccessMutation = useMutation(trpc.vendorManagement.trustApiCheckAccess.mutationOptions())
  const fetchMutation = useMutation(trpc.vendorManagement.trustApiFetch.mutationOptions())

  const invalidateLink = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.vendorManagement.trustApiGetLink.queryKey({ vendorId }),
    })
  const invalidatePreview = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.vendorManagement.trustApiPreview.queryKey({ vendorId }),
    })

  // Seed the selection from the proposed mapping: fetchable rows that satisfy
  // an outstanding request are ticked; everything else is opt-in.
  useEffect(() => {
    if (!preview) return
    setSelected(
      new Set(
        preview.items
          .filter((i) => i.fetchable && i.evidenceRequest)
          .map((i) => i.resource.id)
      )
    )
    setDocTypes(Object.fromEntries(preview.items.map((i) => [i.resource.id, i.docType])))
  }, [preview])

  // Job finished → surface the outcome once and refresh the documents/checklist.
  const jobState = (statusQuery.data as { state?: string; result?: any; error?: string | null } | undefined)
  useEffect(() => {
    if (!jobId || !jobState) return
    if (jobState.state === 'completed') {
      const r = jobState.result ?? {}
      const skipped = Array.isArray(r.skipped) ? r.skipped.length : 0
      toast.success(
        `Fetched ${r.documents ?? 0} document${r.documents === 1 ? '' : 's'} from the Trust Center` +
          (r.evidenceRequestsSatisfied ? ` · ${r.evidenceRequestsSatisfied} evidence request(s) received` : '') +
          (skipped ? ` · ${skipped} skipped` : '')
      )
      setJobId(null)
      onDocumentsChangedRef.current()
      queryClient.invalidateQueries({ queryKey: trpc.vendorManagement.trustApiGetLink.queryKey({ vendorId }) })
      queryClient.invalidateQueries({ queryKey: trpc.vendorManagement.trustApiPreview.queryKey({ vendorId }) })
    } else if (jobState.state === 'failed') {
      toast.error(jobState.error ?? 'Trust Center fetch failed')
      setJobId(null)
    }
  }, [jobId, jobState, queryClient, vendorId])

  const gatedItems = useMemo(
    () => preview?.items.filter((i) => i.resource.access === 'gated') ?? [],
    [preview]
  )
  const needsGrant =
    gatedItems.length > 0 && (!link || ['none', 'denied', 'expired'].includes(link.grantStatus))

  // Only fetchable rows are ever submitted, so the count on the button and the
  // job payload come from the same list.
  const fetchableSelected = useMemo(
    () => (preview?.items ?? []).filter((i) => selected.has(i.resource.id) && i.fetchable),
    [preview, selected]
  )

  const handleLink = async () => {
    if (!url.trim()) return
    try {
      await (setLinkMutation as any).mutateAsync({ vendorId, url: url.trim() })
      setUrl('')
      toast.success('Trust Center API linked')
      invalidateLink()
      setPreviewOpen(true)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not link Trust Center API')
    }
  }

  const handleUnlink = async () => {
    try {
      await (removeLinkMutation as any).mutateAsync({ vendorId })
      setPreviewOpen(false)
      invalidateLink()
      toast.success('Trust Center API unlinked')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not unlink')
    }
  }

  const handleRequestAccess = async () => {
    try {
      await (requestAccessMutation as any).mutateAsync({ vendorId, acceptedNda: ndaAccepted })
      toast.success('Access requested. The publisher has to approve it')
      invalidateLink()
      invalidatePreview()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not request access')
    }
  }

  const handleCheckAccess = async () => {
    try {
      const updated = (await (checkAccessMutation as any).mutateAsync({ vendorId })) as VendorTrustApiLinkDTO
      if (updated.grantStatus === 'granted') toast.success('Access granted')
      else if (updated.grantStatus === 'denied') toast.error('The publisher denied access')
      else toast.info('Still waiting for the publisher to approve')
      invalidateLink()
      invalidatePreview()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not check access')
    }
  }

  const handleFetch = async () => {
    if (!preview?.assessmentId) {
      toast.error('Start an assessment first. Fetched documents attach to its evidence checklist')
      return
    }
    const items = fetchableSelected.map((i) => ({
      resourceId: i.resource.id,
      docType: docTypes[i.resource.id] ?? i.docType,
    }))
    if (items.length === 0) return
    try {
      const { jobId: id } = (await (fetchMutation as any).mutateAsync({
        vendorId,
        assessmentId: preview.assessmentId,
        items,
      })) as { jobId: string }
      setJobId(id)
      toast.info(`Fetching ${items.length} document${items.length === 1 ? '' : 's'} in the background…`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start fetch')
    }
  }

  if (linkQuery.isLoading) {
    return <div className='h-9 w-full animate-pulse rounded-sm bg-muted' />
  }

  if (!link) {
    return (
      <div className='rounded-md border border-border bg-card p-4'>
        <p className='font-body text-[12px] text-text-subtle'>
          If this vendor publishes a machine-readable Trust Center, paste its API
          URL (or its Trust Center domain) to fetch reports straight into the
          evidence checklist.
        </p>
        {!readOnly && (
          <div className='mt-3 flex items-center gap-2'>
            <Input
              className='h-9 flex-1 text-[12px]'
              placeholder='https://api.vendor.example/api/trust/v1/vendor'
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleLink()
                }
              }}
            />
            <Button
              size='sm'
              className='h-9 rounded-sm'
              disabled={!url.trim() || setLinkMutation.isPending}
              onClick={handleLink}
            >
              {setLinkMutation.isPending ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Link2 className='h-3.5 w-3.5' />}
              Link
            </Button>
          </div>
        )}
      </div>
    )
  }

  const attestation = preview?.attestation
  const fetching = Boolean(jobId) && (jobState?.state === 'queued' || jobState?.state === 'running')

  return (
    <div className='rounded-md border border-border bg-card'>
      <div className='flex flex-wrap items-start justify-between gap-3 px-4 py-3'>
        <div className='min-w-0'>
          <p className='font-body text-[13px] font-medium text-text-secondary'>
            {link.publisherName ?? 'Trust Center'}
          </p>
          <p className='font-mono truncate text-[11px] text-text-placeholder'>{link.apiBase}</p>
          <div className='mt-1.5 flex flex-wrap items-center gap-1.5'>
            <GrantBadge status={link.grantStatus} expiresAt={link.grantExpiresAt} />
            {attestation ? (
              attestation.verified ? (
                <Badge status='green' className='gap-1'>
                  <ShieldCheck className='h-3 w-3' /> Signed snapshot verified
                </Badge>
              ) : (
                <Badge status='amber' className='gap-1' title={attestation.errors.join('; ')}>
                  <ShieldAlert className='h-3 w-3' /> Attestation failed
                </Badge>
              )
            ) : link.lastAttestationValid === null ? (
              <Badge status='default'>No signed attestation</Badge>
            ) : null}
            {link.lastFetchedAt ? (
              <span className='font-body text-[11px] text-text-placeholder'>
                Last fetched {new Date(link.lastFetchedAt).toLocaleDateString()}
              </span>
            ) : null}
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-2'>
          <Button
            size='sm'
            variant='outline'
            className='h-8 rounded-sm text-[12px]'
            onClick={() => {
              if (previewOpen) invalidatePreview()
              setPreviewOpen(true)
            }}
            disabled={previewQuery.isFetching}
          >
            {previewQuery.isFetching ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <RefreshCw className='h-3.5 w-3.5' />}
            {previewOpen ? 'Refresh' : 'Preview resources'}
          </Button>
          {!readOnly && (
            <Button
              size='sm'
              variant='ghost'
              className='h-8 rounded-sm text-[12px] text-text-subtle'
              onClick={handleUnlink}
              disabled={removeLinkMutation.isPending}
              title='Unlink this Trust Center API'
            >
              <Unlink className='h-3.5 w-3.5' />
            </Button>
          )}
        </div>
      </div>

      {previewOpen && previewQuery.isError ? (
        <p className='border-t border-border px-4 py-3 text-[12px] text-status-red'>
          {(previewQuery.error as Error)?.message ?? 'Could not reach the Trust Center API'}
        </p>
      ) : null}

      {preview ? (
        <div className='border-t border-border'>
          {!preview.assessmentId ? (
            <p className='px-4 py-2 text-[11px] text-status-orange'>
              No editable assessment. Start one to attach fetched documents to its checklist.
            </p>
          ) : null}

          {!readOnly && needsGrant ? (
            <div className='flex flex-wrap items-center justify-between gap-3 border-b border-border bg-surface-subtle px-4 py-2.5'>
              <label className='flex items-center gap-2 text-[12px] text-text-body'>
                <Checkbox checked={ndaAccepted} onCheckedChange={(v) => setNdaAccepted(v === true)} />
                I acknowledge the publisher's confidentiality terms for gated documents
              </label>
              <Button
                size='sm'
                className='h-8 rounded-sm text-[12px]'
                onClick={handleRequestAccess}
                disabled={!ndaAccepted || requestAccessMutation.isPending}
              >
                {requestAccessMutation.isPending ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : null}
                Request access to {gatedItems.length} gated document{gatedItems.length === 1 ? '' : 's'}
              </Button>
            </div>
          ) : null}

          {!readOnly && link.grantStatus === 'pending' ? (
            <div className='flex items-center justify-between gap-3 border-b border-border bg-surface-subtle px-4 py-2.5'>
              <span className='text-[12px] text-text-subtle'>
                Waiting for the publisher to approve the access request.
              </span>
              <Button size='sm' variant='outline' className='h-8 rounded-sm text-[12px]' onClick={handleCheckAccess} disabled={checkAccessMutation.isPending}>
                {checkAccessMutation.isPending ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : null}
                Check status
              </Button>
            </div>
          ) : null}

          <Table>
            <TableHeader>
              <TableRow className='bg-surface-subtle hover:bg-surface-subtle'>
                <TableHead className='w-8 px-3' />
                <TableHead className='px-3 text-[11px] font-medium text-muted-foreground'>Document</TableHead>
                <TableHead className='px-3 text-[11px] font-medium text-muted-foreground'>Freshness</TableHead>
                <TableHead className='px-3 text-[11px] font-medium text-muted-foreground'>Maps to</TableHead>
                <TableHead className='px-3 text-[11px] font-medium text-muted-foreground'>Evidence request</TableHead>
                <TableHead className='px-3 text-[11px] font-medium text-muted-foreground'>Verified</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className='px-3 py-6 text-center text-[12px] text-text-placeholder'>
                    The publisher lists no documents.
                  </TableCell>
                </TableRow>
              ) : (
                preview.items.map((item) => (
                  <PreviewRow
                    key={item.resource.id}
                    item={item}
                    checked={selected.has(item.resource.id)}
                    docType={docTypes[item.resource.id] ?? item.docType}
                    disabled={readOnly || !item.fetchable}
                    onToggle={(checked) =>
                      setSelected((prev) => {
                        const next = new Set(prev)
                        if (checked) next.add(item.resource.id)
                        else next.delete(item.resource.id)
                        return next
                      })
                    }
                    onDocTypeChange={(v) => setDocTypes((prev) => ({ ...prev, [item.resource.id]: v }))}
                  />
                ))
              )}
            </TableBody>
          </Table>

          {!readOnly ? (
            <div className='flex items-center justify-between gap-3 border-t border-border px-4 py-2.5'>
              <span className='text-[11px] text-text-placeholder'>
                Documents mapped to “Other” are stored but not attached to a request.
              </span>
              <Button
                size='sm'
                className='h-8 rounded-sm text-[12px]'
                onClick={handleFetch}
                disabled={fetching || fetchMutation.isPending || fetchableSelected.length === 0 || !preview.assessmentId}
              >
                {fetching || fetchMutation.isPending ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Download className='h-3.5 w-3.5' />}
                {fetching ? 'Fetching…' : `Fetch ${fetchableSelected.length} selected`}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function GrantBadge({ status, expiresAt }: { status: VendorTrustApiLinkDTO['grantStatus']; expiresAt: string | null }) {
  switch (status) {
    case 'granted':
      return (
        <Badge status='green'>
          Access granted{expiresAt ? ` until ${new Date(expiresAt).toLocaleDateString()}` : ''}
        </Badge>
      )
    case 'pending':
      return <Badge status='amber'>Access pending approval</Badge>
    case 'denied':
      return <Badge status='red'>Access denied</Badge>
    case 'expired':
      return <Badge status='amber'>Access expired</Badge>
    default:
      return <Badge status='default'>Public documents only</Badge>
  }
}

function FreshnessBadge({ status, expiresAt }: { status: string; expiresAt: string | null }) {
  const label = expiresAt ? `${status} · ${new Date(expiresAt).toLocaleDateString()}` : status
  const tone = status === 'current' ? 'green' : status === 'expiring' ? 'amber' : status === 'expired' ? 'red' : 'default'
  return <Badge status={tone}>{label}</Badge>
}

function PreviewRow({
  item,
  checked,
  docType,
  disabled,
  onToggle,
  onDocTypeChange,
}: {
  item: VendorTrustApiPreviewItemDTO
  checked: boolean
  docType: string
  disabled: boolean
  onToggle: (checked: boolean) => void
  onDocTypeChange: (docType: string) => void
}) {
  const r = item.resource
  return (
    <TableRow className={disabled ? 'opacity-60' : undefined}>
      <TableCell className='px-3 py-2'>
        <Checkbox checked={checked} disabled={disabled} onCheckedChange={(v) => onToggle(v === true)} />
      </TableCell>
      <TableCell className='px-3 py-2'>
        <div className='flex max-w-[280px] flex-col gap-0.5'>
          <span className='truncate text-[12px] font-medium text-text-secondary' title={r.title}>
            {r.title}
          </span>
          <span className='flex items-center gap-1 text-[10px] text-text-placeholder'>
            {r.category}
            <Badge status={r.access === 'public' ? 'green' : 'amber'} className='h-4 px-1.5 text-[9px] uppercase'>
              {r.access}
            </Badge>
            {!item.fetchable && r.access === 'gated' ? <span>· needs access</span> : null}
          </span>
        </div>
      </TableCell>
      <TableCell className='px-3 py-2'>
        <FreshnessBadge status={r.freshness.status} expiresAt={r.freshness.expiresAt} />
      </TableCell>
      <TableCell className='px-3 py-2'>
        <Select value={docType} onValueChange={onDocTypeChange} disabled={disabled}>
          <SelectTrigger className='h-7 w-[150px] bg-card text-[11px]'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(DOC_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className='px-3 py-2 text-[11px]'>
        {item.evidenceRequest ? (
          <span className='text-text-body'>{item.evidenceRequest.label}</span>
        ) : (
          <span className='italic text-text-disabled'>—</span>
        )}
      </TableCell>
      <TableCell className='px-3 py-2'>
        {item.attestedSha256 ? (
          <ShieldCheck className='h-4 w-4 text-status-green' aria-label='Hash present in the signed snapshot' />
        ) : (
          <span className='text-[11px] text-text-disabled'>—</span>
        )}
      </TableCell>
    </TableRow>
  )
}
